"use client";

import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

import { SpotifyIcon } from "@/components/icons/spotify-icon";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";
import { client } from "@/utils/orpc.client";

interface SpotifyPlaybackButtonProps {
  className?: string;
  isResolving?: boolean;
  label: string;
  url?: string;
  variant: "compact" | "menu";
  workId?: string;
}

function spotifyPlaybackButtonClassName(
  variant: SpotifyPlaybackButtonProps["variant"],
  className: string | undefined,
): string {
  return cn(
    "flex min-h-11 items-center rounded-lg text-sm font-medium text-[#1DB954] outline-none transition-colors hover:bg-[#1DB954]/10 focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
    variant === "compact"
      ? "size-11 shrink-0 justify-center border border-[#1DB954]/30 bg-[#1DB954]/10 p-0"
      : "w-full gap-2 px-3 py-2.5",
    className,
  );
}

function SpotifyPlaybackButtonContent({
  label,
  variant,
}: Pick<SpotifyPlaybackButtonProps, "label" | "variant">) {
  return (
    <>
      <SpotifyIcon className="shrink-0 text-[#1DB954]" />
      {variant === "menu" && label}
    </>
  );
}

function withReferralParams(url: string): string {
  try {
    const resolvedUrl = new URL(url);
    resolvedUrl.searchParams.set("utm_source", "garimpa.club");
    resolvedUrl.searchParams.set("utm_medium", "referral");
    resolvedUrl.searchParams.set("utm_campaign", "open-on-spotify");
    return resolvedUrl.toString();
  } catch {
    return url;
  }
}

function isPlainLeftClick(event: React.MouseEvent): boolean {
  const isPrimaryButton = event.button === 0;
  const hasModifierKey = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  return isPrimaryButton && !hasModifierKey;
}

function openInNewTab(url: string): void {
  window.open(url, "_blank", "noopener");
}

export function SpotifyPlaybackButton({
  className,
  isResolving = false,
  label,
  url,
  variant,
  workId,
}: SpotifyPlaybackButtonProps) {
  const t = useTranslations("shared.spotifyPlayback");
  const { data: session } = useSession();
  const href = url ? withReferralParams(url) : undefined;
  const playMutation = useMutation({
    mutationFn: async (id: string) => client.music.playSpotifyAlbum({ workId: id }),
    onSuccess: (result) => {
      if (result.status === "ok") {
        toast.success(t("nowPlaying", { device: result.deviceName ?? "" }));
        return;
      }
      if (href) openInNewTab(href);
    },
    onError: () => {
      if (href) openInNewTab(href);
    },
  });
  const sharedClassName = spotifyPlaybackButtonClassName(variant, className);
  const content = <SpotifyPlaybackButtonContent label={label} variant={variant} />;

  if (!href || isResolving || playMutation.isPending) {
    return (
      <button
        type="button"
        disabled
        className={sharedClassName}
        aria-label={label}
        aria-busy={isResolving || undefined}
        title={label}
      >
        {content}
      </button>
    );
  }

  if (!session?.user || workId == null) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener"
        referrerPolicy="origin"
        aria-label={label}
        title={label}
        className={sharedClassName}
      >
        {content}
      </a>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      referrerPolicy="origin"
      aria-label={label}
      title={label}
      onClick={(event) => {
        if (!isPlainLeftClick(event)) {
          return;
        }
        event.preventDefault();
        playMutation.mutate(workId);
      }}
      className={sharedClassName}
    >
      {content}
    </a>
  );
}
